import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Users,
  Building2,
  Landmark,
  Globe,
  User as UserIcon,
  GraduationCap,
  Sparkles,
  Maximize2,
  ExternalLink,
  CheckCircle2,
  Megaphone,
  Pin,
  Flame,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface ComunicadoVisualizacaoData {
  id: string;
  titulo: string;
  corpo?: string;
  mensagem?: string;
  tipo?: string;
  categoria?: string;
  destinatario?: string;
  publicoAlvo?: string;
  fixado?: boolean;
  criadoEm: string;
  lido?: boolean;
  dataEvento?: string | null;
  configPreview?: {
    midiaUrl?: string;
    arquivoUrl?: string;
    bannerUrl?: string;
    corPrimaria?: string;
    corSecundaria?: string;
    corDestaque?: string;
    logoUrl?: string;
    slogan?: string;
    autoridadeNome?: string;
    autoridadeCargo?: string;
    tenantName?: string;
    tenantBadge?: string;
    [key: string]: any;
  } | null;
  turma?: { id?: string; nome?: string } | null;
  escola?: { id?: string; nome?: string; logoUrl?: string } | null;
  municipio?: { id?: string; nome?: string; logoUrl?: string; brasaoUrl?: string } | null;
  estado?: { id?: string; nome?: string; sigla?: string } | null;
  criadoPor?: { id?: string; nome?: string; perfil?: string } | null;
  [key: string]: any;
}

interface Props {
  open: boolean;
  onClose: () => void;
  comunicadoInicial: ComunicadoVisualizacaoData | null;
  listaComunicados?: ComunicadoVisualizacaoData[];
  readIds?: string[];
  onMarkAsRead?: (id: string) => void;
  userPerfil?: string;
  userCargo?: string;
  onViewAllRoute?: string;
}

const CAT_THEMES: Record<string, { gradient: string; accent: string; headerColor: string }> = {
  OPERACIONAL: {
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    accent: '#60a5fa',
    headerColor: '#2563eb',
  },
  INSTITUCIONAL: {
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
    accent: '#a78bfa',
    headerColor: '#7c3aed',
  },
  PEDAGOGICO: {
    gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
    accent: '#6ee7b7',
    headerColor: '#059669',
  },
  URGENTE: {
    gradient: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
    accent: '#fca5a5',
    headerColor: '#dc2626',
  },
};

const DEST_MAP: Record<string, string> = {
  TODOS: 'Toda a Comunidade',
  DIRETORES_EQUIPE: 'Direção & Equipe',
  PROFESSORES: 'Professores',
  RESPONSAVEIS: 'Responsáveis & Famílias',
  ALUNOS: 'Alunos',
  SERIE: 'Série Específica',
  TURMA: 'Turma Específica',
};

function formatEnviadoHa(dateInput: string | Date | undefined): string {
  if (!dateInput) return 'Enviada recentemente';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return 'Enviada recentemente';
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (diffSec < 60) return 'Enviada agora há pouco';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `Enviada há ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `Enviada há ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay === 1) return 'Enviada ontem';
  if (diffDay < 7) return `Enviada há ${diffDay} dias`;
  if (diffDay < 30) {
    const sem = Math.floor(diffDay / 7);
    return `Enviada há ${sem} ${sem === 1 ? 'semana' : 'semanas'}`;
  }
  const meses = Math.floor(diffDay / 30);
  return `Enviada há ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
}

function formatDateTime(dateInput: string | Date | undefined): string {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getMidiaUrl(c: ComunicadoVisualizacaoData | null): string | null {
  if (!c) return null;
  const cfg = c.configPreview;
  const url =
    cfg?.midiaUrl ||
    cfg?.arquivoUrl ||
    cfg?.bannerUrl ||
    c.midiaUrl ||
    c.imagemUrl ||
    c.anexoUrl ||
    c.arquivoUrl ||
    c.fotoUrl;

  if (typeof url === 'string' && url.trim().length > 4) {
    return url.trim();
  }
  return null;
}

export function ComunicadoVisualizacaoModal({
  open,
  onClose,
  comunicadoInicial,
  listaComunicados = [],
  readIds = [],
  onMarkAsRead,
  userPerfil,
  onViewAllRoute,
}: Props) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);

  // Lista normalizada para navegação
  const items = useMemo(() => {
    if (listaComunicados && listaComunicados.length > 0) {
      return listaComunicados;
    }
    return comunicadoInicial ? [comunicadoInicial] : [];
  }, [listaComunicados, comunicadoInicial]);

  // Sincroniza o índice do item quando o comunicado inicial mudar
  useEffect(() => {
    if (!comunicadoInicial) return;
    const idx = items.findIndex((item) => item.id === comunicadoInicial.id);
    if (idx !== -1) {
      setCurrentIndex(idx);
    } else {
      setCurrentIndex(0);
    }
  }, [comunicadoInicial, items]);

  const currentItem = items[currentIndex] || comunicadoInicial;

  // Marca como lido automaticamente ao visualizar
  useEffect(() => {
    if (open && currentItem && onMarkAsRead) {
      if (!readIds.includes(currentItem.id)) {
        onMarkAsRead(currentItem.id);
      }
    }
  }, [open, currentItem, onMarkAsRead, readIds]);

  // Bloqueia scroll do body quando modal está aberto
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Atalhos de teclado (Escape para fechar, setas para navegar)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoomImageUrl) {
          setZoomImageUrl(null);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < items.length - 1) setCurrentIndex((prev) => prev + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, zoomImageUrl, currentIndex, items.length, onClose]);

  if (!open || !currentItem) return null;

  // Resolução de cores, branding e metadados de layout de envio
  const cfg = currentItem.configPreview || {};
  const cat = (currentItem.categoria || 'OPERACIONAL').toUpperCase();
  const isUrgente = currentItem.tipo === 'URGENTE' || cat === 'URGENTE';
  const baseTheme = isUrgente ? CAT_THEMES.URGENTE : (CAT_THEMES[cat] || CAT_THEMES.OPERACIONAL);

  const cor1 = cfg.corPrimaria || (isUrgente ? '#991b1b' : '#1e3a5f');
  const cor2 = cfg.corSecundaria || (isUrgente ? '#ef4444' : '#2563eb');
  const accent = cfg.corDestaque || cfg.corPrimaria || baseTheme.accent;
  const gradient = `linear-gradient(135deg, ${cor1} 0%, ${cor2} 100%)`;

  const midiaUrl = getMidiaUrl(currentItem);

  // Emissor
  const municipioNome = currentItem.municipio?.nome || currentItem.municipioNome;
  const escolaNome = currentItem.escola?.nome || currentItem.escolaNome;
  const estadoNome = currentItem.estado?.nome || currentItem.estadoSigla;
  const autorNome = currentItem.criadoPor?.nome || currentItem.autorNome;

  const tenantName =
    cfg.tenantName ||
    (municipioNome ? `Prefeitura de ${municipioNome}` : escolaNome ? escolaNome : 'Instituição de Ensino Oficial');

  const tenantBadge =
    cfg.tenantBadge ||
    (municipioNome ? 'Secretaria Municipal de Educação' : escolaNome ? 'Direção / Coordenação Escolar' : estadoNome ? `Governo Estadual (${estadoNome})` : 'Comunicado do Sistema');

  const logoUrl =
    cfg.logoUrl ||
    currentItem.escola?.logoUrl ||
    currentItem.municipio?.logoUrl ||
    currentItem.municipio?.brasaoUrl;

  // Destinatários e Direcionamento
  const rawDest = (currentItem.destinatario || currentItem.publicoAlvo || 'TODOS').toUpperCase();
  const destLabel = DEST_MAP[rawDest] || rawDest;
  const turmaNome = currentItem.turma?.nome || currentItem.turmaNome;

  let isDirectMatch = false;
  if (rawDest.includes('PROFESSOR') && userPerfil === 'PROFESSOR') isDirectMatch = true;
  if ((rawDest.includes('RESPONSAVEL') || rawDest.includes('PAIS')) && userPerfil === 'RESPONSAVEL') isDirectMatch = true;
  if (rawDest.includes('ALUNO') && userPerfil === 'ALUNO') isDirectMatch = true;
  if ((rawDest.includes('EQUIPE') || rawDest.includes('DIRECAO')) && ['ADMIN', 'ESCOLA', 'EQUIPE', 'MUNICIPAL'].includes(userPerfil || '')) isDirectMatch = true;

  const isCurrentUnread = !readIds.includes(currentItem.id);
  const totalItems = items.length;
  const unreadCount = items.filter((it) => !readIds.includes(it.id)).length;

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleViewAll = () => {
    onClose();
    if (onViewAllRoute) {
      navigate(onViewAllRoute);
      return;
    }
    const path = window.location.pathname;
    if (userPerfil === 'RESPONSAVEL' || path.includes('/responsavel')) {
      navigate('/responsavel/comunicados');
      return;
    }
    if (userPerfil === 'PROFESSOR' || path.includes('/professor')) {
      navigate('/professor/comunicados');
      return;
    }
    const base = path.split('/')[1] || 'admin';
    navigate(`/${base}/comunicados`);
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99998,
          padding: '16px 12px',
          animation: 'ac-fade-in 0.2s ease-out',
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: 'hsl(var(--card))',
            borderRadius: 18,
            width: '100%',
            maxWidth: 680,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.4)',
            border: '1px solid hsl(var(--border))',
            position: 'relative',
            animation: 'ac-scale-in 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ─── BARRA DE NAVEGAÇÃO ENTRE COMUNICADOS (TOP TOOLBAR) ─── */}
          <div
            style={{
              padding: '6px 12px',
              background: '#0f172a',
              color: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '.74rem',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              gap: 8,
              minHeight: 40,
            }}
          >
            {/* Controles de Folheamento / Paginação */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button
                type="button"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                style={{
                  background: currentIndex === 0 ? 'transparent' : 'rgba(255,255,255,0.12)',
                  color: currentIndex === 0 ? '#475569' : '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 7px',
                  cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  fontWeight: 700,
                  fontSize: '.70rem',
                }}
                title="Comunicado anterior"
              >
                <ChevronLeft size={15} />
                <span>Ant</span>
              </button>

              <span style={{ color: '#94a3b8', padding: '0 4px', fontWeight: 700, fontSize: '.70rem' }}>
                {currentIndex + 1} / {totalItems}
              </span>

              <button
                type="button"
                onClick={handleNext}
                disabled={currentIndex === items.length - 1}
                style={{
                  background: currentIndex === items.length - 1 ? 'transparent' : 'rgba(255,255,255,0.12)',
                  color: currentIndex === items.length - 1 ? '#475569' : '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 7px',
                  cursor: currentIndex === items.length - 1 ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  fontWeight: 700,
                  fontSize: '.70rem',
                }}
                title="Próximo comunicado"
              >
                <span>Próx</span>
                <ChevronRight size={15} />
              </button>
            </div>

            {/* Botão de Fechar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '50%',
                  width: 26,
                  height: 26,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                title="Fechar (Esc)"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* ─── CORPO COM SCROLL INTERNO E LAYOUT DE ENVIO ─── */}
          <div style={{ overflowY: 'auto', flex: 1, padding: 0 }}>
            {/* Header com Branding Oficial / Brasão / Cores de Envio */}
            <div
              style={{
                background: gradient,
                padding: '16px 18px',
                color: '#ffffff',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Efeitos decorativos sutis */}
              <div
                style={{
                  position: 'absolute',
                  right: -30,
                  top: -30,
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.08)',
                  pointerEvents: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: 80,
                  bottom: -40,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.05)',
                  pointerEvents: 'none',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, position: 'relative', zIndex: 1 }}>
                {/* Logo / Brasão + Nome da Instituição */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: '#ffffff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: '2px solid rgba(255,255,255,0.8)',
                    }}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Brasão"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Building2 size={22} color={cor1} />
                    )}
                  </div>

                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.98rem', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.25, wordBreak: 'break-word' }}>
                      {tenantName}
                    </div>
                    <div style={{ fontSize: '.72rem', opacity: 0.85, fontWeight: 500, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tenantBadge}
                    </div>
                  </div>
                </div>

                {/* Badge de Tipo / Categoria */}
                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.22)',
                    backdropFilter: 'blur(6px)',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    color: '#ffffff',
                    padding: '4px 10px',
                    borderRadius: 20,
                    fontSize: '.65rem',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                    alignSelf: 'flex-start',
                  }}
                >
                  <Megaphone size={12} />
                  {currentItem.tipo || 'COMUNICADO'}
                </div>
              </div>
            </div>

            {/* Imagem / Folder em Destaque (se houver foto ou anexo) — dobro do tamanho e destaque visual */}
            {midiaUrl && (
              <div
                onClick={() => setZoomImageUrl(midiaUrl)}
                style={{
                  width: '100%',
                  height: 'clamp(240px, 36vh, 460px)',
                  background: '#090d16',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'zoom-in',
                  borderBottom: '1px solid hsl(var(--border))',
                }}
                title="Clique para ampliar a imagem"
              >
                <img
                  src={midiaUrl}
                  alt={currentItem.titulo}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    background: 'rgba(0,0,0,0.65)',
                    backdropFilter: 'blur(6px)',
                    color: '#ffffff',
                    padding: '5px 10px',
                    borderRadius: 8,
                    fontSize: '.70rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  <Maximize2 size={13} /> Clique para ampliar
                </div>
              </div>
            )}

            {/* Conteúdo Central do Comunicado */}
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Título do Comunicado */}
              <div>
                <h1
                  style={{
                    margin: '0 0 6px',
                    fontSize: '1.2rem',
                    fontWeight: 800,
                    color: 'hsl(var(--foreground))',
                    lineHeight: 1.3,
                  }}
                >
                  {currentItem.titulo}
                </h1>
                <div
                  style={{
                    width: 44,
                    height: 3.5,
                    borderRadius: 2,
                    background: accent,
                  }}
                />
              </div>

              {/* Badges de Metadados: Emissor, Destinatário e Data/Hora */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 6,
                  fontSize: '.72rem',
                }}
              >
                {/* Badge de Destinatário */}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: 'hsl(var(--secondary))',
                    color: 'hsl(var(--secondary-foreground))',
                    fontWeight: 700,
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  <Users size={12} style={{ color: accent }} />
                  Para: {destLabel}
                  {turmaNome ? ` · Turma ${turmaNome}` : ''}
                </span>

                {/* Destaque Direcionado */}
                {isDirectMatch && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: 'linear-gradient(135deg, #fef08a 0%, #fde047 100%)',
                      color: '#854d0e',
                      fontWeight: 800,
                      border: '1px solid #facc15',
                      boxShadow: '0 1px 3px rgba(234, 179, 8, 0.2)',
                    }}
                  >
                    <Sparkles size={12} />
                    Direcionado para você
                  </span>
                )}

                {/* Emissor / Autor */}
                {autorNome && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: 'hsl(var(--muted))',
                      color: 'hsl(var(--muted-foreground))',
                      fontWeight: 600,
                    }}
                  >
                    <UserIcon size={12} />
                    Por: {autorNome}
                  </span>
                )}

                {/* Data do Evento se houver */}
                {currentItem.dataEvento && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: '#ecfdf5',
                      color: '#065f46',
                      fontWeight: 700,
                      border: '1px solid #a7f3d0',
                    }}
                  >
                    <Calendar size={12} />
                    Evento: {new Date(currentItem.dataEvento).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>

              {/* Mensagem Completa */}
              <div
                style={{
                  fontSize: '.88rem',
                  lineHeight: 1.6,
                  color: 'hsl(var(--foreground))',
                  whiteSpace: 'pre-wrap',
                  background: 'hsl(var(--muted))',
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid hsl(var(--border))',
                }}
              >
                {currentItem.corpo || currentItem.mensagem || 'Conteúdo da mensagem não disponível.'}
              </div>

              {/* Data e Hora de Envio Relativa + Slogan Compacto */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 6,
                  fontSize: '.72rem',
                  color: 'hsl(var(--muted-foreground))',
                  paddingTop: 4,
                  borderTop: '1px solid hsl(var(--border))',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Clock size={12} style={{ color: accent }} />
                  {formatEnviadoHa(currentItem.criadoEm)}
                </span>

                {cfg.slogan && (
                  <span style={{ fontStyle: 'italic', opacity: 0.85, fontSize: '.70rem' }}>
                    "{cfg.slogan}"
                  </span>
                )}
              </div>

              {/* Assinatura da Autoridade / Gestor Compacta */}
              {cfg.autoridadeNome && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '6px 12px',
                    background: 'hsl(var(--muted))',
                    borderRadius: 8,
                    border: '1px solid hsl(var(--border))',
                  }}
                >
                  <strong style={{ fontSize: '.78rem', color: 'hsl(var(--foreground))' }}>
                    {cfg.autoridadeNome}
                  </strong>
                  <span style={{ fontSize: '.70rem', color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>
                    {cfg.autoridadeCargo || 'Gestor Responsável'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ─── FOOTER COM AÇÕES E NAVEGAÇÃO ─── */}
          <div
            style={{
              padding: '12px 20px',
              background: 'hsl(var(--card))',
              borderTop: '1px solid hsl(var(--border))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            {/* Link para ver todos os comunicados */}
            <button
              type="button"
              onClick={handleViewAll}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                fontSize: '.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              <FileText size={14} /> Ver todos os comunicados
            </button>

            {/* Botões Fechar e Próximo Não Lido */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {unreadCount > 0 && currentIndex < items.length - 1 && (
                <button
                  type="button"
                  onClick={handleNext}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    color: '#ffffff',
                    fontSize: '.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                  }}
                >
                  Próximo Não Lido <ChevronRight size={14} />
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 18px',
                  borderRadius: 8,
                  border: '1.5px solid hsl(var(--border))',
                  background: 'hsl(var(--muted))',
                  color: 'hsl(var(--foreground))',
                  fontSize: '.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── LIGHTBOX FULLSCREEN PARA ZOOM DA IMAGEM ─── */}
      {zoomImageUrl && (
        <div
          onClick={() => setZoomImageUrl(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.88)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100000,
            padding: 20,
            cursor: 'zoom-out',
          }}
        >
          <button
            type="button"
            onClick={() => setZoomImageUrl(null)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={20} />
          </button>
          <img
            src={zoomImageUrl}
            alt="Mídia em alta resolução"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
    </>
  );
}
